import React, { useCallback, useEffect, useRef, useState } from "react";
import { CustomBreadcrumb } from "../custom-breadcrumb/custom-breadcrumb";
import { CustomTable } from "../custom-table/custom-table";
import { SERVICES } from "../../../core/services/services";
import { CustomLoader } from "../custom-loader/custom-loader";
import { CustomModal } from "../custom-modal/custom-modal";
import { Toast } from "primereact/toast";
import { HELPER } from "../../helper/helper";
import { MandatesFilter } from "./mandates-filter";
import { MandateDetails } from "./mandate-details";
import ErrorBoundary from "../error-boundary/error-boundary";
import { BACKOFFICE_API } from "../../../core/apis/backoffice-apis";
import { UserTypes } from "../../constants";
import { FRONTOFFICE_API } from "../../../core/apis/frontoffice-apis";
import { Optional } from "../optional/optional";

export function Mandates(props) {
  const toast = useRef(null);
  const [visible, setVisible] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [error, setError] = useState("");
  const [search, setSearch] = useState(false);
  const [totalPages, setTotalPages] = useState(0);
  const [totalItems, setTotalItems] = useState(0);
  const [range] = useState(5);
  const [emptyText, setEmptyText] = useState("");
  const [currentModalIndex, setCurrentModalIndex] = useState(0);
  const [debitMandates, setDebitMandates] = useState([]);
  const [pageNo, setPageNo] = useState(0);
  const [searchParams, setSearchParams] = useState(null);
  const [selectedMandate, setSelectedMandate] = useState(null);

  const tableHeaders = [
    { label: "Name", value: "name" },
    { label: "Account Number", value: "accountNumber" },
    {
      label: "Reference",
      value: "reference",
    },
    { label: "Payment Facilitator", value: "paymentFacilitatorName" },
    { label: "Merchant Name", value: "merchantName" },
    { label: "Status", value: "status" },
    { label: "Created On", value: "creationDate" },
    { label: "Expires On", value: "expiryDate" },
    { label: "Actions", value: "actions" },
  ];

  const getMandateDetails = useCallback((mandate) => {
    setSelectedMandate(mandate);
    setCurrentIndex(3);
  }, []);

  const openAction = useCallback(
    (e, action, isMobile) => {
      // eslint-disable-next-line default-case
      switch (action) {
        case "VIEW": {
          getMandateDetails(e, isMobile);
          break;
        }
      }
    },
    [getMandateDetails]
  );

  function goToPage(pageNo) {
    setPageNo(pageNo);
  }

  const getDebitMandates = useCallback(
    (isReload) => {
      setCurrentIndex(0);
      setDebitMandates([]);
      let params = {
        page: isReload ? 0 : pageNo,
        size: 10,
      };
      if (searchParams && !isReload) {
        setSearch(true);
        params = { ...params, ...searchParams };
      }
      params = HELPER.TO_URL_STRING(params);
      const url =
        props.userType === UserTypes.Frontoffice
          ? FRONTOFFICE_API.DEBIT_MANDATES
          : BACKOFFICE_API.DEBIT_MANDATES;
      SERVICES.MAKE_GET_REQUEST(url, params)
        .then((data) => {
          const result = data?.result?.content;
          if (!result.length) {
            setEmptyText("No mandate records found ...");
          } else {
            let arr = [];
            setTotalItems(data?.result.totalElements);
            setTotalPages(data?.result.totalPages);
            result.forEach((e) => {
              arr.push({
                ...e,
                actions: "CR",
                detailsFunction: openAction,
              });
            });
            setDebitMandates(arr);
          }
          setError(null);
          setCurrentIndex(1);
        })
        .catch((error) => {
          setError(HELPER.PROCESS_ERROR(error));
          setCurrentIndex(1);
        });
    },
    [pageNo, searchParams, openAction, props.userType]
  );

  useEffect(() => {
    getDebitMandates();
  }, [getDebitMandates]);

  function onHide() {}

  function openModal(index, isMobile) {
    setCurrentModalIndex(index);
    if (isMobile) {
      setCurrentIndex(2);
    } else {
      setVisible(true);
    }
  }

  function reload() {
    const isReload = true;
    setCurrentIndex(0);
    getDebitMandates(isReload);
    setSearchParams(null);
  }

  function closeModal(isReload) {
    if (visible) {
      setVisible(false);
    }
    if (currentIndex === 2) {
      setCurrentIndex(1);
    }
    if (isReload === true) {
      reload();
    }
  }

  function goBack(isReload) {
    if (isReload === true) {
      getDebitMandates();
      return;
    }
    setCurrentIndex(1);
  }

  function saveSearchParams(params) {
    setVisible(false);
    setPageNo(0);
    setSearchParams(params);
  }

  const modalContent = () => {
    // eslint-disable-next-line default-case
    switch (currentModalIndex) {
      case 1:
        return (
          <MandatesFilter
            searchFunction={saveSearchParams}
            closeModal={closeModal}
            userType={props.userType}
          />
        );
    }
  };

  const renderMandates = () => {
    // eslint-disable-next-line default-case
    switch (currentIndex) {
      case 0:
        return (
          <div className="loading-container">
            <CustomLoader loadingText="Loading mandates..." />
          </div>
        );
      case 1:
        return (
          <CustomTable
            isReload={true}
            totalPages={totalPages}
            totalItems={totalItems}
            currentPage={pageNo + 1}
            range={range}
            emptyText={emptyText}
            search={search}
            reload={reload}
            error={error}
            items={debitMandates}
            headers={tableHeaders}
            nextPage={() => setPageNo(pageNo + 1)}
            prevPage={() => setPageNo(pageNo - 1)}
            goToFirstPage={() => setPageNo(0)}
            goToLastPage={() => setPageNo(totalPages - 1)}
            goToPage={goToPage}
          />
        );
      case 2:
        return (
          <div className="mobile-modal-container">
            <div className="custom-card">
              <div className="mobile-card-position">{modalContent()}</div>
            </div>
          </div>
        );
      case 3:
        return (
          <div className="p-mt-2">
            <MandateDetails
              goBack={goBack}
              mandateReference={selectedMandate?.reference}
              userType={props.userType}
              onReload={reload}
            />
          </div>
        );
    }
  };

  return (
    <ErrorBoundary>
      <div>
        <Toast ref={toast} />
        <div>
          <CustomModal
            closeModal={closeModal}
            onHide={onHide}
            visible={visible}
            modalContent={modalContent}
          />
        </div>
        <div className="page-title p-text-left">Mandates</div>
        <div className="p-mt-2">
          <CustomBreadcrumb page="Manage Mandates" />
        </div>
        <div className="floating-buttons desktop-scree">
          <div className="p-grid">
            <div className="p-col-7"></div>
            <div className="p-col-5">
              <div className="p-grid">
                <div className="p-col-6"></div>
                <div className="p-col-6">
                  <Optional
                    show={
                      (HELPER.HAS_AUTHORITY("fo_view_debit_mandates") ||
                        HELPER.HAS_AUTHORITY("bo_view_debit_mandates")) &&
                      currentIndex === 1
                    }
                  >
                    <button
                      disabled={currentIndex === 0}
                      onClick={() => openModal(1, false)}
                      className="primary-button"
                    >
                      <i className="pi pi-filter" />
                      <span className="hide-btn-text"> Filter</span>
                    </button>
                  </Optional>
                </div>
              </div>
            </div>
          </div>
        </div>
        <Optional
          show={
            HELPER.HAS_AUTHORITY("fo_view_debit_mandates") ||
            HELPER.HAS_AUTHORITY("bo_view_debit_mandates")
          }
        >
          {renderMandates()}
        </Optional>
      </div>
    </ErrorBoundary>
  );
}
